import Modal from '@components/v2/ui/Modal/Modal';
import type { ModalProps } from '@components/v2/ui/Modal/modal.types';
import Typography from '@components/v2/ui/Typography/Typography';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { makeStyles } from 'tss-react/mui';
import LangEnum from '../../../lang/LangEnum';
import useBrandSplitSettings from '../../brand-split-settings/useBrandSplitSettings';
import { useTranslation } from '../../translation/TranslationContext';

const useStyles = makeStyles()(() => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
  },
  infoBox: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    backgroundColor: '#EEEEEE',
    padding: '8px 12px',
    borderRadius: 8,
  },
  infoIcon: {
    marginTop: 2,
  },
}));

export type ConfirmOrderSplitModalProps = {
  orderCode?: string;
  onConfirm: () => void;
  isMassAction?: boolean;
};

type OrderSplitConfirmModal = Pick<ModalProps, 'isOpen' | 'onClose'> & ConfirmOrderSplitModalProps;

const OrderSplitConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  orderCode,
  isMassAction,
}: OrderSplitConfirmModal) => {
  const { selectedSplitSettings } = useBrandSplitSettings();
  const { translate } = useTranslation();
  const { classes } = useStyles();

  if (!selectedSplitSettings.length) {
    return <></>;
  }

  return (
    <Modal
      isOpen={isOpen}
      size="standard"
      header={{
        title: isMassAction
          ? translate(LangEnum.ORDER_SPLIT_CONFIRM_TITLE_MASS_ACTION)
          : translate(LangEnum.ORDER_SPLIT_CONFIRM_TITLE, { orderCode }),
      }}
      onClose={onClose}
      footer={{
        primaryAction: {
          label: translate(LangEnum.GLOBAL_CONFIRM_LABEL),
          onClick: () => {
            onConfirm();
            onClose();
          },
        },
        secondaryAction: {
          label: translate(LangEnum.GLOBAL_CANCEL_LABEL),
          onClick: onClose,
        },
      }}
    >
      <Box className={classes.container}>
        {isMassAction ? (
          <Typography variant="body1" color="text.secondary">
            <FormattedMessage
              id={LangEnum.ORDER_SPLIT_CONFIRM_DESCRIPTION_MASS_ACTION}
              values={{
                splitSettings: selectedSplitSettings.map((setting) => setting.label).join(', '),
                b: (chunks: React.ReactNode) => (
                  <Typography component="span" variant="body1">
                    {chunks}
                  </Typography>
                ),
              }}
            />
          </Typography>
        ) : (
          <Typography variant="body1" color="text.secondary">
            <FormattedMessage
              id={LangEnum.ORDER_SPLIT_CONFIRM_DESCRIPTION}
              values={{
                splitSettings: selectedSplitSettings.map((setting) => setting.label).join(', '),
                b: (chunks: React.ReactNode) => (
                  <Typography component="span" variant="body1">
                    {chunks}
                  </Typography>
                ),
              }}
            />
          </Typography>
        )}

        <Box className={classes.infoBox}>
          <InfoOutlinedIcon fontSize="small" className={classes.infoIcon} />
          <Typography variant="body2">
            <Typography component="span" variant="body2">
              {translate(LangEnum.ORDER_SPLIT_IRREVERSIBLE_LABEL)}
            </Typography>{' '}
            {translate(LangEnum.ORDER_SPLIT_IRREVERSIBLE_NOTICE)}
          </Typography>
        </Box>
      </Box>
    </Modal>
  );
};

export default OrderSplitConfirmModal;
